import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { Button } from 'reactstrap';
import { AutocompleteChips } from '../../../../shared/components';
import { User } from '../../../../services/types/User';

export interface InviteReviewersFormData {
  members: User[];
}

interface Props {
  handleSubmit: (values: InviteReviewersFormData, resetFormCallback: () => void) => void;
  handleCancel: () => void;
  handleMembersSearch: (egsMemberQuery: string) => void;
  members: User[] | null;
  loadingMembers: boolean;
  formState?: InviteReviewersFormData;
  isCof: boolean | null;
}

const formSchema = Yup.object().shape({
  members: Yup.array().min(1, 'You must select at least one reviewer'),
});

const InviteReviewersForm = (props: Props) => {
  const {
    handleSubmit,
    handleCancel,
    formState,
    handleMembersSearch,
    members,
    loadingMembers,
    isCof,
  } = props;
  const { t } = useTranslation();
  const formInit: InviteReviewersFormData = {
    members: formState ? formState.members : [],
  };

  return (
    <Formik
      initialValues={formInit}
      validationSchema={formSchema}
      onSubmit={(values, { resetForm }) => {
        const payload = { ...values } as InviteReviewersFormData;
        handleSubmit(payload, resetForm);
      }}
      onReset={() => {
        // Formik's resetForm() already invokes this onReset handler itself
        // (see formik/dist/formik.cjs.development.js) - calling resetForm()
        // again from in here re-enters it and recurses until the call stack
        // overflows. handleReset (bound to the form's native reset event)
        // already calls resetForm() before this runs, so nothing here needs
        // to reset the form again.
        handleCancel();
      }}
    >
      {(formik) => (
        <form className="form" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
          <div className="modal__body">
            <div className="spr-modal modal-dialog--custom-filter">
              <div className="form-row">
                <div className="col-sm-12">
                  <p className="form__text--readonly">
                    {t(
                      'Please add the reviewer(s) for this supervision report. Please note that the ESG Primary Team member is from {{location}}.',
                      { location: isCof ? 'COF' : 'HQ' }
                    )}
                  </p>
                </div>
              </div>
              <div className="form-row">
                <div className="col-sm-12">
                  <AutocompleteChips<User>
                    text={<>{t('addReviewers')}</>}
                    labelKey="fullName"
                    htmlId="egs-members-typeahead"
                    list={members}
                    placeholder="Ex. John Smith"
                    onSearch={handleMembersSearch}
                    onChange={(selectedMembers: User[]) => {
                      formik.setFieldValue('members', selectedMembers);
                    }}
                    onBlur={() => {
                      formik.setTouched({ ...formik.touched, members: [] });
                    }}
                    loading={loadingMembers}
                    render={(option: User) => <div key={option.email}>{option.fullName}</div>}
                    className="label_autocomplete"
                    multiple
                  />
                </div>
                {formik.touched.members && formik.errors.members && (
                  <div className="col-sm-12">
                    <span className="form__form-group-error">
                      {formik.errors.members as string}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="modal__footer modal__foote--custom-filter">
            <Button key="btnCancel" className="btn-tertiary" type="reset">
              {t('cancel')}
            </Button>
            <Button
              key="btnSave"
              className="btn-primary"
              type="submit"
              disabled={!formik.isValid}
              color="primary"
            >
              {t('send')}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default InviteReviewersForm;
